import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { getApiEndpoints, getSelectedEnvironment } from '../utils/apiConfig';

const STATUS_LABELS = {
  PENDING:    { label: 'Pending',    color: '#ff9800' },
  PROCESSING: { label: 'Processing', color: '#2196f3' },
  COMPLETED:  { label: 'Completed',  color: '#4caf50' },
  PARTIAL:    { label: 'Partial',    color: '#ff9800' },
  FAILED:     { label: 'Failed',     color: '#f44336' },
};

const JOB_TYPE_LABELS = {
  AGENT_UPLOAD: 'Agent Bulk Upload',
  AGENT_UPDATE: 'Agent Bulk Update',
  PROGRAM_DATA: 'Program Data Upload',
  QUICK_CONNECT: 'Quick Connect Setup',
  ROUTING_PROFILE: 'Routing Profile Setup',
  SECURITY_PROFILE: 'Security Profile Setup',
  USER_HIERARCHY_GROUP: 'User Hierarchy Group Setup',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const BulkJobDetailPage = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiEndpoints = getApiEndpoints(getSelectedEnvironment());
  const BULK_STATUS_API = apiEndpoints.bulkStatusApi;

  const fetchJob = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${BULK_STATUS_API}/${jobId}`);
      let data = response.data;
      if (data.body && typeof data.body === 'string') {
        try { data = JSON.parse(data.body); } catch (_) {}
      }
      if (data.status === 'ok') {
        setJob(data.job);
      } else {
        setError(data.message || 'Failed to load job');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [BULK_STATUS_API, jobId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const statusInfo = job ? (STATUS_LABELS[job.status] || { label: job.status, color: '#607d8b' }) : null;

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Bulk Job Detail</h1>
          <p className="page-subtitle" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{jobId}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={fetchJob} className="btn btn-secondary" disabled={loading}>
            {loading ? 'Loading...' : '↻ Refresh'}
          </button>
          <Link to="/bulk-jobs" className="btn btn-secondary">← Back to Jobs</Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading job details...</p>
        </div>
      )}

      {!loading && job && (
        <>
          {/* Summary card */}
          <div className="form-container" style={{ marginBottom: '1.5rem' }}>
            <div className="form-section">
              <h3>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  backgroundColor: statusInfo.color,
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  marginRight: '0.75rem',
                }}>
                  {statusInfo.label}
                </span>
                Job Summary
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.75rem' }}>
                <InfoRow label="Submitted By" value={job.submitted_by || '—'} />
                <InfoRow label="Job Type" value={JOB_TYPE_LABELS[job.job_type || 'AGENT_UPLOAD'] || (job.job_type || 'AGENT_UPLOAD')} />
                {(job.job_type || 'AGENT_UPLOAD') !== 'PROGRAM_DATA' && (
                  <InfoRow label="Instance ID" value={job.instance_id || '—'} mono />
                )}
                <InfoRow label="Created" value={formatDate(job.created_at)} />
                <InfoRow label="Last Updated" value={formatDate(job.updated_at)} />
                <InfoRow label="Completed" value={formatDate(job.completed_at)} />
              </div>
            </div>

            <div className="form-section">
              <h3>Progress</h3>
              {/* Progress bar */}
              <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '14px', margin: '0.5rem 0 0.25rem' }}>
                <div style={{
                  height: '100%',
                  borderRadius: '4px',
                  width: `${job.progress_pct || 0}%`,
                  backgroundColor: job.status === 'FAILED' ? '#f44336' : job.status === 'PARTIAL' ? '#ff9800' : '#4caf50',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                <span><strong>Total:</strong> {job.total}</span>
                <span><strong>Processed:</strong> {job.processed}</span>
                <span style={{ color: '#4caf50' }}><strong>✅ Succeeded:</strong> {job.succeeded}</span>
                <span style={{ color: job.failed > 0 ? '#f44336' : 'inherit' }}><strong>❌ Failed:</strong> {job.failed}</span>
                <span><strong>Progress:</strong> {Math.round(job.progress_pct || 0)}%</span>
              </div>
            </div>
          </div>

          {/* Error list */}
          {job.error_count > 0 && (
            <div className="form-container">
              <div className="form-section">
                <h3>
                  Failed Records
                  <span style={{ fontWeight: 'normal', fontSize: '0.85rem', marginLeft: '0.5rem', color: '#666' }}>
                    ({job.error_count} total{job.errors_truncated ? ', showing first 200' : ''})
                  </span>
                </h3>
                <div className="error-list" style={{ maxHeight: '500px', overflowY: 'auto', marginTop: '0.5rem' }}>
                  {(job.errors || []).map((err, idx) => (
                    <div key={idx} className="error-item">
                      {err.row && <span className="error-row">Row {err.row}: </span>}
                      {err.user_login && <span className="error-field">{err.user_login} — </span>}
                      <span className="error-message">
                        {Array.isArray(err.errors) ? err.errors.join('; ') : (err.error || err.message || JSON.stringify(err))}
                      </span>
                    </div>
                  ))}
                </div>
                {job.errors_truncated && (
                  <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
                    ⚠️ Only the first 200 errors are stored. Re-upload the failed records after correcting them.
                  </p>
                )}
              </div>
            </div>
          )}

          {job.error_count === 0 && job.status !== 'PENDING' && job.status !== 'PROCESSING' && (
            <div className="alert alert-success" style={{ marginTop: '0.5rem' }}>
              <p>No errors — all records processed successfully.</p>
            </div>
          )}

          {(job.status === 'PENDING' || job.status === 'PROCESSING') && (
            <div className="alert alert-info" style={{ marginTop: '0.5rem' }}>
              <p>Job is still in progress. Use the Refresh button to check the latest status.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function InfoRow({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.78rem' : '0.9rem', wordBreak: 'break-all' }}>
        {value}
      </div>
    </div>
  );
}

export default BulkJobDetailPage;
